import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { tenantProfessionsApi, type ProfessionHolder } from "../../api/tenantProfessionsApi.js";
import { toast } from "../Toast.js";
import { Button } from "../ui/Button.js";

const inputStyle: React.CSSProperties = {
  height: 32,
  border: "1px solid #cbd5e1",
  borderRadius: 8,
  padding: "0 10px",
  fontSize: 13,
  fontFamily: "inherit",
  background: "#fff",
  boxSizing: "border-box",
  width: "100%",
};

type EditState = {
  name: string;
  professional_registration_number: string;
  invoice_registration_number: string;
};

function toEditState(holder: ProfessionHolder): EditState {
  return {
    name: holder.name,
    professional_registration_number: holder.professional_registration_number ?? "",
    invoice_registration_number: holder.invoice_registration_number ?? "",
  };
}

/**
 * 資格種別（組み込み3種＋カスタム）1件あたりの資格者名簿（tenant_profession_holders）。
 * 氏名・士業登録番号・適格請求書発行事業者番号を直接編集できる
 * （利用ユーザーへのリンクはしない。docs/design/user-professional-separation.md）。
 *
 * 「資格の種類」画面（ProfessionMasterPanel）とテンプレート編集の発行者情報ダイアログ
 * （IssuerMasterDialog）の両方から、同じこのコンポーネントを共有する。
 */
export function ProfessionHolderRoster({ profession }: { profession: string }) {
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState<EditState>({ name: "", professional_registration_number: "", invoice_registration_number: "" });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<EditState>({ name: "", professional_registration_number: "", invoice_registration_number: "" });

  const holdersQuery = useQuery({
    queryKey: ["tenant-profession-holders", profession],
    queryFn: () => tenantProfessionsApi.listHolders(profession),
  });
  const holders = holdersQuery.data?.data ?? [];

  const addMut = useMutation({
    mutationFn: (body: EditState) =>
      tenantProfessionsApi.addHolder(profession, {
        name: body.name.trim(),
        professional_registration_number: body.professional_registration_number.trim() || null,
        invoice_registration_number: body.invoice_registration_number.trim() || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenant-profession-holders", profession] });
      qc.invalidateQueries({ queryKey: ["tenant-professions"] });
      setAddForm({ name: "", professional_registration_number: "", invoice_registration_number: "" });
      setAddOpen(false);
      toast("資格者を追加しました");
    },
    onError: (e: Error) => toast(e.message, "error"),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: EditState }) =>
      tenantProfessionsApi.updateHolder(profession, id, {
        name: body.name.trim(),
        professional_registration_number: body.professional_registration_number.trim() || null,
        invoice_registration_number: body.invoice_registration_number.trim() || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenant-profession-holders", profession] });
      qc.invalidateQueries({ queryKey: ["tenant-professions"] });
      setEditingId(null);
      toast("資格者を更新しました");
    },
    onError: (e: Error) => toast(e.message, "error"),
  });

  const removeMut = useMutation({
    mutationFn: (holderId: string) => tenantProfessionsApi.removeHolder(profession, holderId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenant-profession-holders", profession] });
      qc.invalidateQueries({ queryKey: ["tenant-professions"] });
      toast("資格者を削除しました");
    },
    onError: (e: Error) => toast(e.message, "error"),
  });

  function startEdit(holder: ProfessionHolder) {
    setEditingId(holder.id);
    setEditForm(toEditState(holder));
  }

  return (
    <div style={{ padding: "12px 16px", borderTop: "1px solid #f1f5f9" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: 12, color: "#64748b", fontWeight: 800 }}>
            資格者（この資格で帳票に印字できる人。複数登録できます）
          </div>
          {/* この一覧は行単位で即時保存する。カード上部の「設定を保存」は対象外なので、
              押す必要がないことを明記する（設計書 §8.2-2）。 */}
          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
            追加・編集した内容はその場で保存されます（上の「設定を保存」は不要です）
          </div>
        </div>
        <Button onClick={() => setAddOpen((v) => !v)} style={{ whiteSpace: "nowrap" }}>
          {addOpen ? "閉じる" : "＋ 資格者を追加"}
        </Button>
      </div>

      {holdersQuery.isLoading ? (
        <div style={{ fontSize: 12, color: "#94a3b8" }}>読み込み中...</div>
      ) : (
        <div style={{ display: "grid", gap: 8 }}>
          {holders.length === 0 && !addOpen && (
            <div style={{ fontSize: 12, color: "#94a3b8" }}>まだ資格者が登録されていません。</div>
          )}
          {holders.map((h) => {
            const isEditing = editingId === h.id;
            return (
              <div key={h.id} style={{
                padding: "8px 10px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8,
              }}>
                {isEditing ? (
                  <div style={{ display: "grid", gap: 6 }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
                      <input
                        style={inputStyle}
                        placeholder="氏名"
                        value={editForm.name}
                        onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                      />
                      <input
                        style={inputStyle}
                        placeholder="登録番号（例: 愛知県第123号）"
                        value={editForm.professional_registration_number}
                        onChange={(e) => setEditForm((f) => ({ ...f, professional_registration_number: e.target.value }))}
                      />
                      <input
                        style={inputStyle}
                        placeholder="適格請求書発行事業者番号"
                        value={editForm.invoice_registration_number}
                        onChange={(e) => setEditForm((f) => ({ ...f, invoice_registration_number: e.target.value }))}
                      />
                    </div>
                    <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                      <Button onClick={() => setEditingId(null)}>取消</Button>
                      <Button
                        variant="primary"
                        disabled={!editForm.name.trim() || updateMut.isPending}
                        title={editForm.name.trim() ? undefined : "氏名を入力してください"}
                        onClick={() => updateMut.mutate({ id: h.id, body: editForm })}
                      >
                        {updateMut.isPending ? "保存中..." : "この資格者を保存"}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{h.name}</span>
                      {h.is_default && (
                        <span style={{
                          fontSize: 10, fontWeight: 700, color: "#0f766e", background: "#f0fdfa",
                          border: "1px solid #99f6e4", borderRadius: 99, padding: "1px 7px",
                        }}>
                          既定
                        </span>
                      )}
                      {h.professional_registration_number && (
                        <span style={{ fontSize: 11, color: "#94a3b8" }}>{h.professional_registration_number}</span>
                      )}
                      {h.invoice_registration_number && (
                        <span style={{ fontSize: 11, color: "#94a3b8" }}>{h.invoice_registration_number}</span>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <Button onClick={() => startEdit(h)}>編集</Button>
                      <Button
                        variant="danger"
                        disabled={h.is_default || removeMut.isPending}
                        title={h.is_default ? "既定の資格者は削除できません。上の「既定の資格者」で別の人を選び、「設定を保存」してから削除してください" : undefined}
                        onClick={() => {
                          if (confirm(`「${h.name}」を削除しますか？`)) removeMut.mutate(h.id);
                        }}
                      >
                        削除
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {addOpen && (
            <div style={{ padding: "8px 10px", background: "#f8fafc", border: "1px dashed #cbd5e1", borderRadius: 8 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
                <input
                  style={inputStyle}
                  placeholder="氏名 *"
                  value={addForm.name}
                  onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))}
                />
                <input
                  style={inputStyle}
                  placeholder="登録番号（例: 愛知県第123号）"
                  value={addForm.professional_registration_number}
                  onChange={(e) => setAddForm((f) => ({ ...f, professional_registration_number: e.target.value }))}
                />
                <input
                  style={inputStyle}
                  placeholder="適格請求書発行事業者番号"
                  value={addForm.invoice_registration_number}
                  onChange={(e) => setAddForm((f) => ({ ...f, invoice_registration_number: e.target.value }))}
                />
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, marginTop: 6 }}>
                <span style={{ fontSize: 11, color: "#94a3b8" }}>
                  {addForm.name.trim() ? "「この資格者を追加」で保存されます" : "氏名を入力してください"}
                </span>
                <Button
                  variant="primary"
                  disabled={!addForm.name.trim() || addMut.isPending}
                  title={addForm.name.trim() ? undefined : "氏名を入力してください"}
                  onClick={() => addMut.mutate(addForm)}
                >
                  {addMut.isPending ? "追加中..." : "この資格者を追加"}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
